import { MessageResponse } from '@crowdex/ui/ai/message';
import { Button } from '@crowdex/ui/components/button';
import { Textarea } from '@crowdex/ui/components/textarea';
import {
  type PullRequestReviewThread,
  providerCommentMarkdown,
} from '@crowdex/workspace-protocol/pull-request-cockpit';
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Columns2,
  ListFilter,
  MessageSquare,
  Minus,
  Plus,
  Reply,
  Undo2,
  WrapText,
} from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { swipesFrom } from '@/components/mobile-shell/use-horizontal-drag';
import { EDGE_WIDTH } from '@/components/mobile-shell/use-swipe-back';
import { useMediaQuery } from '@/hooks/use-media-query';
import { DESKTOP_LAYOUT_QUERY } from '@/lib/desktop-shell/desktop-layout';
import { parseReviewDiff, type ReviewDiffFile, type ReviewDiffLine } from '@/lib/review-diff';

export function DesktopGitDiff({
  label = 'Uncommitted',
  onSubmitReview,
  onFileAction,
  onReplyThread,
  onResolveThread,
  onToggleSource,
  reviewThreads = [],
  value,
  columns = false,
  footerActions,
}: {
  /**
   * Review's own layout (D24): the files in a column beside the diff of the one picked, and a
   * footer with the pending comments and `footerActions` (Discard file, Create PR).
   */
  columns?: boolean;
  footerActions?: ((path: string) => ReactNode) | undefined;
  label?: string;
  onSubmitReview: (review: string) => void;
  onFileAction?: (path: string, action: 'stage' | 'unstage' | 'revert') => void;
  onReplyThread?: (threadId: string, body: string) => Promise<void>;
  onResolveThread?: (threadId: string) => Promise<void>;
  onToggleSource?: () => void;
  reviewThreads?: readonly PullRequestReviewThread[];
  value: string;
}) {
  const files = parseReviewDiff(value);
  const linesAdded = files.reduce((total, file) => total + file.additions, 0);
  const linesDeleted = files.reduce((total, file) => total + file.deletions, 0);
  const initiallyOpen = files.length <= 25 && linesAdded + linesDeleted <= 2000;
  const [openState] = useState<{ open: boolean; revision: number }>({
    open: initiallyOpen,
    revision: 0,
  });
  const [viewType, setViewType] = useState<'split' | 'unified'>('unified');
  const [wrapChoice, setWrap] = useState(false);
  // A phone lists the files, then shows one at a time, unified and wrapped.
  const phone = !useMediaQuery(DESKTOP_LAYOUT_QUERY);
  const wrap = phone || wrapChoice;
  const [shownFile, setShownFile] = useState<number | null>(null);
  // A phone opens on the first file's diff; its list is one tap away (mobile 21).
  const [phoneList, setPhoneList] = useState(false);
  const phoneIndex = phoneList ? null : Math.min(shownFile ?? 0, Math.max(files.length - 1, 0));
  const shownIndex = phone ? phoneIndex : shownFile;
  const shown = shownIndex === null ? undefined : files[shownIndex];
  const [comments, setComments] = useState<Record<string, string>>({});
  const commentCount = Object.values(comments).filter(
    (comment) => comment.trim().length > 0,
  ).length;
  const readonlyCommentCount = reviewThreads.reduce(
    (total, thread) => total + thread.comments.length,
    0,
  );
  const submitReview = () => {
    const review = Object.entries(comments)
      .filter(([, comment]) => comment.trim().length > 0)
      .map(([location, comment]) => `- ${location}: ${comment.trim()}`)
      .join('\n');
    onSubmitReview(`Please address these review comments:\n\n${review}`);
    setComments({});
  };
  if (columns && !phone) {
    const index = shownFile !== null && shownFile < files.length ? shownFile : 0;
    const file = files[index];
    return (
      <div className="flex h-full min-h-0 flex-col bg-background">
        <div className="flex min-h-0 flex-1">
          <aside className="flex w-56 shrink-0 flex-col border-hairline border-r">
            <div className="type-meta flex items-center gap-2 px-3 pt-1 pb-2">
              <button
                className="flex min-w-0 items-center gap-1 rounded-md text-foreground hover:text-foreground disabled:pointer-events-none"
                disabled={onToggleSource === undefined}
                onClick={onToggleSource}
                type="button"
              >
                <span className="truncate">
                  {files.length} {files.length === 1 ? 'file' : 'files'}
                </span>
                {onToggleSource !== undefined && <ChevronDown className="size-3" />}
              </button>
              <span className="ml-auto font-mono text-desktop-success">+{linesAdded}</span>
              <span className="font-mono text-destructive">−{linesDeleted}</span>
            </div>
            <ul aria-label="Changed files" className="min-h-0 flex-1 overflow-y-auto px-1.5 pb-2">
              {files.map((candidate, fileIndex) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: A diff can name a path twice; its files are positions in it.
                <li key={`${candidate.path}-${fileIndex}`}>
                  <button
                    aria-current={fileIndex === index || undefined}
                    className={`flex w-full flex-col rounded-lg px-2.5 py-1.5 text-left ${
                      fileIndex === index ? 'bg-control' : 'hover:bg-control/60'
                    }`}
                    onClick={() => setShownFile(fileIndex)}
                    type="button"
                  >
                    <span className="truncate font-medium font-mono text-xs">
                      {candidate.path.split('/').at(-1)}
                    </span>
                    <span className="type-caption flex items-center gap-1.5 text-muted-foreground">
                      <span className="min-w-0 flex-1 truncate">
                        {candidate.path.split('/').slice(-3, -1).join('/') || '/'}
                      </span>
                      <span className="font-mono text-desktop-success">+{candidate.additions}</span>
                      <span className="font-mono text-destructive">−{candidate.deletions}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>
          <section aria-label="Diff" className="flex min-w-0 flex-1 flex-col">
            <div className="flex shrink-0 items-center gap-2 px-3 pt-1 pb-2">
              <span className="min-w-0 flex-1 truncate font-mono text-xs">{file?.path}</span>
              <div className="flex rounded-lg bg-control p-0.5">
                {(['unified', 'split'] as const).map((type) => (
                  <button
                    aria-pressed={viewType === type}
                    className="type-meta h-7 rounded-control px-3 text-muted-foreground aria-pressed:bg-background aria-pressed:font-semibold aria-pressed:text-foreground"
                    key={type}
                    onClick={() => setViewType(type)}
                    type="button"
                  >
                    {type === 'unified' ? 'Unified' : 'Split'}
                  </button>
                ))}
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto pb-3">
              {file !== undefined && (
                <DesktopGitDiffFile
                  file={file}
                  initialOpen
                  key={`${file.path}-${index}`}
                  comments={comments}
                  onCommentChange={(key, comment) =>
                    setComments((current) => ({ ...current, [key]: comment }))
                  }
                  onFileAction={onFileAction}
                  onReplyThread={onReplyThread}
                  onResolveThread={onResolveThread}
                  openState={openState}
                  reviewThreads={reviewThreads.filter((thread) => thread.path === file.path)}
                  viewType={viewType}
                  wrap={wrap}
                />
              )}
            </div>
          </section>
        </div>
        <footer className="flex shrink-0 items-center gap-2 border-hairline border-t px-4 py-2.5">
          <span className="type-meta min-w-0 flex-1 truncate text-muted-foreground">
            {commentCount > 0
              ? `${commentCount} ${commentCount === 1 ? 'comment' : 'comments'} pending · `
              : ''}
            Click a line to comment
          </span>
          {commentCount > 0 && (
            <Button className="rounded-control" onClick={submitReview} size="sm" variant="outline">
              Send comments
            </Button>
          )}
          {file !== undefined && footerActions?.(file.path)}
        </footer>
      </div>
    );
  }
  const showFile = (index: number) => {
    setShownFile(index);
    setPhoneList(false);
  };
  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div
        className={
          phone
            ? // A phone's narrow row wraps rather than squeezing the source away.
              'flex shrink-0 flex-wrap items-center justify-between gap-2 px-3 py-2'
            : 'flex shrink-0 items-center justify-between py-2 pr-2 pl-6'
        }
        hidden={phone && shown !== undefined}
      >
        <div
          className={`flex min-w-0 items-center gap-2 text-sm ${phone ? 'flex-wrap gap-y-1 [&>span]:whitespace-nowrap' : ''}`}
        >
          <button
            className={`flex min-w-0 items-center gap-1.5 rounded-md px-1.5 py-1 text-foreground hover:bg-accent disabled:pointer-events-none ${phone ? 'shrink-0' : ''}`}
            disabled={onToggleSource === undefined}
            onClick={onToggleSource}
            type="button"
          >
            <span className="truncate">{label}</span>
            {onToggleSource !== undefined && <ChevronDown className="size-3" />}
          </button>
          <span className="text-muted-foreground">
            {files.length} {files.length === 1 ? 'file changed' : 'files changed'}
          </span>
          <span className="font-mono text-desktop-success">+{linesAdded}</span>
          <span className="font-mono text-destructive">−{linesDeleted}</span>
          {commentCount + readonlyCommentCount > 0 && (
            <span className="text-muted-foreground">
              {commentCount + readonlyCommentCount} comments
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <div className={phone ? 'hidden' : 'flex rounded-md bg-muted p-0.5'}>
            <Button
              aria-label="Switch to unified diff"
              className="size-control-compact"
              onClick={() => setViewType('unified')}
              size="icon-sm"
              variant={viewType === 'unified' ? 'secondary' : 'ghost'}
            >
              <ListFilter className="size-3.5" />
            </Button>
            <Button
              aria-label="Switch to split diff"
              className="size-control-compact"
              onClick={() => setViewType('split')}
              size="icon-sm"
              variant={viewType === 'split' ? 'secondary' : 'ghost'}
            >
              <Columns2 className="size-3.5" />
            </Button>
          </div>
          <Button
            aria-label={wrap ? 'Disable word wrap' : 'Enable word wrap'}
            hidden={phone}
            aria-pressed={wrap}
            className="size-control-compact"
            onClick={() => setWrap((current) => !current)}
            size="icon-sm"
            variant={wrap ? 'secondary' : 'ghost'}
          >
            <WrapText className="size-3.5" />
          </Button>
          {commentCount > 0 &&
            (phone ? (
              // The words of the file's own footer.
              <Button className="rounded-control" onClick={submitReview} size="sm">
                Send {commentCount} {commentCount === 1 ? 'comment' : 'comments'}
              </Button>
            ) : (
              <Button onClick={submitReview} size="sm">
                Submit review
              </Button>
            ))}
        </div>
      </div>
      {phone && shown === undefined ? (
        <ul aria-label="Changed files" className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
          {files.map((file, fileIndex) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: A diff can name a path twice; its files are positions in it.
            <li key={`${file.path}-${fileIndex}`}>
              <button
                className="flex min-h-14 w-full items-center gap-3 rounded-xl px-3 text-left active:bg-accent/60"
                onClick={() => showFile(fileIndex)}
                type="button"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-sm">
                    {file.path.split('/').at(-1)}
                  </span>
                  <span className="type-caption block truncate text-muted-foreground">
                    {file.path.split('/').slice(0, -1).join('/') || '/'}
                  </span>
                </span>
                <span className="shrink-0 font-mono text-desktop-success text-xs">
                  +{file.additions}
                </span>
                <span className="shrink-0 font-mono text-destructive text-xs">
                  −{file.deletions}
                </span>
                <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
              </button>
            </li>
          ))}
        </ul>
      ) : phone && shown !== undefined && shownIndex !== null ? (
        <PhoneDiffFile
          count={files.length}
          file={shown}
          footer={
            <>
              {commentCount > 0 && (
                <Button className="rounded-control" onClick={submitReview} variant="outline">
                  Send {commentCount} {commentCount === 1 ? 'comment' : 'comments'}
                </Button>
              )}
              {footerActions?.(shown.path)}
            </>
          }
          index={shownIndex}
          onBack={() => setPhoneList(true)}
          onShow={showFile}
        >
          <DesktopGitDiffFile
            file={shown}
            initialOpen
            key={`${shown.path}-${shownIndex}`}
            comments={comments}
            onCommentChange={(key, comment) =>
              setComments((current) => ({ ...current, [key]: comment }))
            }
            onFileAction={onFileAction}
            onReplyThread={onReplyThread}
            onResolveThread={onResolveThread}
            openState={openState}
            reviewThreads={reviewThreads.filter((thread) => thread.path === shown.path)}
            viewType="unified"
            wrap
          />
        </PhoneDiffFile>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto pb-3">
          <div>
            {files.map((file, fileIndex) => (
              <DesktopGitDiffFile
                file={file}
                initialOpen={initiallyOpen}
                // biome-ignore lint/suspicious/noArrayIndexKey: A diff can name a path twice; its files are positions in it.
                key={`${file.path}-${fileIndex}`}
                comments={comments}
                onCommentChange={(key, comment) =>
                  setComments((current) => ({ ...current, [key]: comment }))
                }
                onFileAction={onFileAction}
                onReplyThread={onReplyThread}
                onResolveThread={onResolveThread}
                openState={openState}
                reviewThreads={reviewThreads.filter((thread) => thread.path === file.path)}
                viewType={viewType}
                wrap={wrap}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** How far a finger swipes sideways to show the next or the previous file. */
const FILE_SWIPE_DISTANCE = 64;

/**
 * One changed file on a phone (mobile 21), under a bar with its neighbours on each side and its
 * name in the middle (which leads to the list of files); a sideways swipe shows the next or the
 * previous one. Its actions sit under it, within the thumb's reach (the shell keeps them clear of
 * the home indicator).
 */
function PhoneDiffFile({
  index,
  count,
  file,
  footer,
  onBack,
  onShow,
  children,
}: {
  index: number;
  count: number;
  file: ReviewDiffFile;
  footer: ReactNode;
  onBack: () => void;
  onShow: (index: number) => void;
  children: ReactNode;
}) {
  const start = useRef<{ x: number; y: number } | null>(null);
  const show = (next: number) => {
    if (next >= 0 && next < count) onShow(next);
  };
  const folder = file.path.split('/').slice(-3, -1).join('/') || '/';
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center gap-2 border-hairline border-b px-3 pb-3">
        <Button
          aria-label="Previous file"
          className="shrink-0 bg-control"
          disabled={index === 0}
          onClick={() => show(index - 1)}
          size="icon"
          variant="ghost"
        >
          <ChevronLeft />
        </Button>
        <button
          aria-label={`Changed files: ${file.path}, ${index + 1} of ${count}`}
          className="flex min-w-0 flex-1 items-center gap-2 rounded-2xl bg-control px-3 py-1.5 text-left active:bg-control/70"
          data-review-file-chip
          onClick={onBack}
          type="button"
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium font-mono text-sm">
              {file.path.split('/').at(-1)}
            </span>
            <span className="type-caption block truncate text-muted-foreground">
              {folder} · {index + 1} of {count}
            </span>
          </span>
          <span className="shrink-0 font-mono text-desktop-success text-xs">+{file.additions}</span>
          <span className="shrink-0 font-mono text-destructive text-xs">−{file.deletions}</span>
        </button>
        <Button
          aria-label="Next file"
          className="shrink-0 bg-control"
          disabled={index === count - 1}
          onClick={() => show(index + 1)}
          size="icon"
          variant="ghost"
        >
          <ChevronRight />
        </Button>
      </div>
      <div
        className="min-h-0 flex-1 overflow-y-auto pb-3"
        onTouchEnd={(event) => {
          const began = start.current;
          const touch = event.changedTouches[0];
          start.current = null;
          if (began === null || touch === undefined) return;
          const dx = touch.clientX - began.x;
          const dy = touch.clientY - began.y;
          if (Math.abs(dx) < FILE_SWIPE_DISTANCE || Math.abs(dx) < 2 * Math.abs(dy)) return;
          show(dx < 0 ? index + 1 : index - 1);
        }}
        onTouchStart={(event) => {
          const touch = event.touches[0];
          // The left edge goes back to the chat; a field or sideways content keeps its finger.
          start.current =
            event.touches.length === 1 &&
            touch !== undefined &&
            touch.clientX > EDGE_WIDTH &&
            swipesFrom(event.target)
              ? { x: touch.clientX, y: touch.clientY }
              : null;
        }}
      >
        {children}
      </div>
      <footer
        className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-hairline border-t px-3 py-2.5 empty:hidden [&>button]:h-11 [&>button]:flex-1"
        data-review-footer
      >
        {footer}
      </footer>
    </div>
  );
}

function DesktopGitDiffFile({
  file,
  initialOpen,
  comments,
  onCommentChange,
  onFileAction,
  onReplyThread,
  onResolveThread,
  openState,
  reviewThreads,
  viewType,
  wrap,
}: {
  file: ReviewDiffFile;
  initialOpen: boolean;
  comments: Record<string, string>;
  onCommentChange: (key: string, comment: string) => void;
  onFileAction?: (path: string, action: 'stage' | 'unstage' | 'revert') => void;
  onReplyThread?: (threadId: string, body: string) => Promise<void>;
  onResolveThread?: (threadId: string) => Promise<void>;
  openState: { open: boolean; revision: number };
  reviewThreads: readonly PullRequestReviewThread[];
  viewType: 'split' | 'unified';
  wrap: boolean;
}) {
  const [open, setOpen] = useState(initialOpen);
  useEffect(() => setOpen(openState.open), [openState]);
  return (
    <section
      className="mx-2 mt-2 scroll-mt-2 overflow-hidden rounded-md border border-hairline bg-desktop-terminal"
      id={`review-file-${encodeURIComponent(file.path)}`}
    >
      <div className="group flex w-full items-center gap-2 border-hairline border-b bg-muted/30 px-3 py-2 text-left">
        <button
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          onClick={() => setOpen((value) => !value)}
          type="button"
        >
          {open ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
          <span className="min-w-0 flex-1 truncate font-mono text-xs">{file.path}</span>
        </button>
        {file.additions > 0 && (
          <span className="font-mono text-desktop-success text-xs">+{file.additions}</span>
        )}
        {file.deletions > 0 && (
          <span className="font-mono text-destructive text-xs">−{file.deletions}</span>
        )}
        {onFileAction !== undefined && (
          <span className="ml-2 flex items-center gap-0.5 can-hover:opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
            <Button
              aria-label={`Stage ${file.path}`}
              className="h-control-xs gap-1 px-1.5 text-caption"
              onClick={(event) => {
                event.stopPropagation();
                onFileAction(file.path, 'stage');
              }}
              size="sm"
              variant="ghost"
            >
              <Plus className="size-3" /> Stage
            </Button>
            <Button
              aria-label={`Unstage ${file.path}`}
              className="size-control-xs"
              onClick={(event) => {
                event.stopPropagation();
                onFileAction(file.path, 'unstage');
              }}
              size="icon-sm"
              title="Unstage file"
              variant="ghost"
            >
              <Minus className="size-3" />
            </Button>
            <Button
              aria-label={`Revert ${file.path}`}
              className="size-control-xs text-destructive hover:text-destructive"
              onClick={(event) => {
                event.stopPropagation();
                // The caller asks first: it knows whose the file is.
                onFileAction(file.path, 'revert');
              }}
              size="icon-sm"
              title="Revert working tree changes"
              variant="ghost"
            >
              <Undo2 className="size-3" />
            </Button>
          </span>
        )}
      </div>
      {open && (
        <div
          className={`overflow-x-auto font-mono text-xs leading-5 ${viewType === 'split' ? 'grid grid-cols-2' : ''}`}
        >
          {viewType === 'split' ? (
            <>
              <div className="min-w-0 border-hairline border-r">
                {file.lines
                  .filter((line) => line.kind !== 'addition')
                  .map((line, index) => (
                    <DesktopGitDiffLine
                      comment={comments[`${file.path}:old:${line.oldLine ?? index}`] ?? ''}
                      commentKey={`${file.path}:old:${line.oldLine ?? index}`}
                      // biome-ignore lint/suspicious/noArrayIndexKey: A hunk's lines are positions in the parsed diff (headers and collapsed lines have no number).
                      key={`old-${index}`}
                      line={line}
                      onCommentChange={onCommentChange}
                      onReplyThread={onReplyThread}
                      onResolveThread={onResolveThread}
                      reviewThreads={reviewThreadsForLine(reviewThreads, line, 'old')}
                      side="old"
                      wrap={wrap}
                    />
                  ))}
              </div>
              <div className="min-w-0">
                {file.lines
                  .filter((line) => line.kind !== 'deletion')
                  .map((line, index) => (
                    <DesktopGitDiffLine
                      comment={comments[`${file.path}:new:${line.newLine ?? index}`] ?? ''}
                      commentKey={`${file.path}:new:${line.newLine ?? index}`}
                      // biome-ignore lint/suspicious/noArrayIndexKey: A hunk's lines are positions in the parsed diff (headers and collapsed lines have no number).
                      key={`new-${index}`}
                      line={line}
                      onCommentChange={onCommentChange}
                      onReplyThread={onReplyThread}
                      onResolveThread={onResolveThread}
                      reviewThreads={reviewThreadsForLine(reviewThreads, line, 'new')}
                      side="new"
                      wrap={wrap}
                    />
                  ))}
              </div>
            </>
          ) : (
            file.lines.map((line, index) => {
              // A removed line and the line added in its place share a number: its side tells
              // them apart (as Split keys them).
              const commentKey =
                line.kind === 'deletion'
                  ? `${file.path}:old:${line.oldLine ?? index}`
                  : `${file.path}:new:${line.newLine ?? line.oldLine ?? index}`;
              return (
                <DesktopGitDiffLine
                  comment={comments[commentKey] ?? ''}
                  commentKey={commentKey}
                  // biome-ignore lint/suspicious/noArrayIndexKey: A hunk's lines are positions in the parsed diff (headers and collapsed lines have no number).
                  key={index}
                  line={line}
                  onCommentChange={onCommentChange}
                  onReplyThread={onReplyThread}
                  onResolveThread={onResolveThread}
                  reviewThreads={reviewThreadsForLine(reviewThreads, line, 'unified')}
                  side="unified"
                  wrap={wrap}
                />
              );
            })
          )}
          {reviewThreads
            .filter(
              (thread) =>
                !file.lines.some(
                  (line) => reviewThreadsForLine([thread], line, 'unified').length > 0,
                ),
            )
            .map((thread) => (
              <div className={viewType === 'split' ? 'col-span-2' : undefined} key={thread.id}>
                <div className="border-hairline border-y bg-muted/35 px-3 py-1.5 font-sans text-caption text-muted-foreground">
                  Comment on {thread.path}
                  {(thread.line ?? thread.startLine) !== null
                    ? `:${thread.line ?? thread.startLine}`
                    : ''}
                  {thread.isOutdated ? ' · Outdated' : ''}
                </div>
                <ReadonlyReviewThread
                  onReply={onReplyThread}
                  onResolve={onResolveThread}
                  side="unified"
                  thread={thread}
                />
              </div>
            ))}
        </div>
      )}
    </section>
  );
}

function DesktopGitDiffLine({
  comment,
  commentKey,
  line,
  onCommentChange,
  onReplyThread,
  onResolveThread,
  reviewThreads,
  side,
  wrap,
}: {
  comment: string;
  commentKey: string;
  line: ReviewDiffLine;
  onCommentChange: (key: string, comment: string) => void;
  onReplyThread?: (threadId: string, body: string) => Promise<void>;
  onResolveThread?: (threadId: string) => Promise<void>;
  reviewThreads: readonly PullRequestReviewThread[];
  side: 'new' | 'old' | 'unified';
  wrap: boolean;
}) {
  const [commenting, setCommenting] = useState(comment.length > 0);
  const [draft, setDraft] = useState(comment);
  if (line.kind === 'hunk') return null;
  if (line.kind === 'collapsed') {
    return (
      <div className="flex h-7 items-center border-hairline-faint border-y bg-muted/45 text-muted-foreground">
        <span className="flex w-8 items-center justify-center border-hairline border-r">
          <ChevronDown className="size-3.5" />
        </span>
        <span className="px-3 text-xs">{line.content}</span>
      </div>
    );
  }
  const background =
    line.kind === 'addition'
      ? 'bg-desktop-success/20'
      : line.kind === 'deletion'
        ? 'bg-destructive/20'
        : '';
  const marker = line.kind === 'addition' ? '+' : line.kind === 'deletion' ? '−' : ' ';
  return (
    <div className={background}>
      <div className={`group flex ${wrap ? 'min-w-0' : 'min-w-max'}`}>
        {side === 'unified' && (
          <span className="w-10 shrink-0 select-none border-hairline-faint border-r pr-2 text-right text-muted-foreground">
            {line.oldLine ?? ''}
          </span>
        )}
        <span className="w-10 shrink-0 select-none border-hairline-faint border-r pr-2 text-right text-muted-foreground">
          {side === 'old' ? (line.oldLine ?? '') : (line.newLine ?? '')}
        </span>
        <span className="w-6 shrink-0 select-none text-center text-muted-foreground">
          {line.kind === 'meta' ? '' : marker}
        </span>
        <span
          className={`min-w-0 flex-1 px-1 text-foreground/80 ${wrap ? 'whitespace-pre-wrap break-all' : 'whitespace-pre'}`}
        >
          {highlightReviewCode(line.content) || ' '}
        </span>
        {line.kind !== 'meta' && (
          <button
            aria-label="Add a comment to this line"
            className="sticky right-1 my-0.5 mr-1 flex size-5 shrink-0 items-center justify-center rounded bg-background text-muted-foreground can-hover:opacity-0 shadow-sm ring-1 ring-border hover:text-foreground group-hover:opacity-100 focus-visible:opacity-100"
            onClick={() => setCommenting(true)}
            type="button"
          >
            <MessageSquare className="size-3" />
          </button>
        )}
      </div>
      {commenting && (
        <div
          className={`my-2 mr-3 max-w-xl rounded-lg border border-hairline-strong bg-card p-2.5 font-sans shadow-lg ${
            side === 'unified' ? 'ml-20' : 'ml-10'
          }`}
        >
          <Textarea
            aria-label="Review comment"
            className="min-h-16 resize-y rounded-md border-hairline-strong bg-background/70 px-3 py-2 text-sm leading-5 placeholder:text-muted-foreground/65"
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Leave a comment on this line…"
            value={draft}
          />
          <div className="mt-2 flex items-center justify-end gap-1.5">
            <Button
              className="h-control-compact px-2.5 text-xs"
              onClick={() => {
                setDraft(comment);
                setCommenting(false);
              }}
              size="sm"
              variant="ghost"
            >
              Cancel
            </Button>
            <Button
              className="h-control-compact px-3 text-xs"
              disabled={draft.trim().length === 0}
              onClick={() => {
                onCommentChange(commentKey, draft.trim());
                setCommenting(false);
              }}
              size="sm"
              variant="secondary"
            >
              Save comment
            </Button>
          </div>
        </div>
      )}
      {!commenting && comment.length > 0 && (
        // A saved comment stays under its line until the review is sent; a tap edits it.
        <button
          aria-label={`Edit the comment: ${comment}`}
          className={`my-1.5 mr-3 flex max-w-xl items-start gap-2 rounded-lg border border-hairline-strong bg-card px-2.5 py-2 text-left font-sans text-foreground/90 text-xs leading-5 hover:bg-control/60 ${
            side === 'unified' ? 'ml-20' : 'ml-10'
          }`}
          onClick={() => {
            setDraft(comment);
            setCommenting(true);
          }}
          type="button"
        >
          <MessageSquare className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
          <span className="min-w-0 whitespace-pre-wrap break-words">{comment}</span>
        </button>
      )}
      {reviewThreads.map((thread) => (
        <ReadonlyReviewThread
          key={thread.id}
          onReply={onReplyThread}
          onResolve={onResolveThread}
          side={side}
          thread={thread}
        />
      ))}
    </div>
  );
}

function reviewThreadsForLine(
  threads: readonly PullRequestReviewThread[],
  line: ReviewDiffLine,
  side: 'new' | 'old' | 'unified',
): PullRequestReviewThread[] {
  return threads.filter((thread) => {
    const target = thread.line ?? thread.startLine;
    if (target === null) return false;
    if (side === 'old') return thread.side === 'left' && line.oldLine === target;
    if (side === 'new') return thread.side !== 'left' && line.newLine === target;
    return thread.side === 'left' ? line.oldLine === target : line.newLine === target;
  });
}

function ReadonlyReviewThread({
  onReply,
  onResolve,
  side,
  thread,
}: {
  onReply?: (threadId: string, body: string) => Promise<void>;
  onResolve?: (threadId: string) => Promise<void>;
  side: 'new' | 'old' | 'unified';
  thread: PullRequestReviewThread;
}) {
  const [replying, setReplying] = useState(false);
  const [reply, setReply] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const indent = side === 'unified' ? 'ml-20' : 'ml-10';

  async function submitReply(): Promise<void> {
    if (onReply === undefined || reply.trim().length === 0 || working) return;
    setWorking(true);
    setError(null);
    try {
      await onReply(thread.id, reply.trim());
      setReply('');
      setReplying(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not post the reply.');
    } finally {
      setWorking(false);
    }
  }

  async function resolve(): Promise<void> {
    if (onResolve === undefined || working) return;
    setWorking(true);
    setError(null);
    try {
      await onResolve(thread.id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not resolve the thread.');
    } finally {
      setWorking(false);
    }
  }

  return (
    <div
      className={`${indent} mr-3 border-hairline-strong border-y bg-background font-sans ${
        thread.isResolved ? 'opacity-70' : ''
      }`}
    >
      <div className="divide-y divide-hairline-faint">
        {thread.comments.map((comment) => (
          <article className="flex gap-2.5 px-3 py-3" key={comment.id}>
            {comment.author.avatarUrl === null ? (
              <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted type-caption text-muted-foreground ring-1 ring-hairline">
                {comment.author.login.slice(0, 1).toUpperCase()}
              </span>
            ) : (
              <img
                alt=""
                className="size-6 shrink-0 rounded-full bg-muted object-cover ring-1 ring-hairline"
                height={24}
                src={comment.author.avatarUrl}
                width={24}
              />
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-xs">
                <span className="truncate font-medium">{comment.author.login}</span>
                {comment.createdAt !== null && (
                  <time className="ml-auto shrink-0 text-caption text-muted-foreground">
                    {comment.createdAt.slice(0, 10)}
                  </time>
                )}
              </div>
              <MessageResponse className="mt-1.5 type-ui text-foreground/90 text-xs leading-5">
                {providerCommentMarkdown(comment.body)}
              </MessageResponse>
            </div>
          </article>
        ))}
      </div>
      {replying && (
        <div className="border-hairline border-t px-3 py-2.5">
          <Textarea
            aria-label="Reply to review thread"
            className="min-h-16 resize-y bg-background text-sm"
            onChange={(event) => setReply(event.target.value)}
            placeholder="Add a reply…"
            value={reply}
          />
          <div className="mt-2 flex justify-end gap-1.5">
            <Button
              className="h-control-compact text-xs"
              disabled={working}
              onClick={() => setReplying(false)}
              size="sm"
              variant="ghost"
            >
              Cancel
            </Button>
            <Button
              className="h-control-compact text-xs"
              disabled={working || reply.trim().length === 0}
              onClick={() => void submitReply()}
              size="sm"
              variant="secondary"
            >
              Reply
            </Button>
          </div>
        </div>
      )}
      {error !== null && <p className="px-3 pb-2 text-destructive text-xs">{error}</p>}
      <div className="flex w-full items-start gap-2 py-1.5 ps-8.5">
        {onReply !== undefined && !replying && (
          <Button
            className="h-control-compact gap-1.5 px-2 text-xs"
            disabled={working}
            onClick={() => setReplying(true)}
            size="sm"
            variant="ghost"
          >
            <Reply className="size-3.5" /> Reply
          </Button>
        )}
        {thread.isResolved ? (
          <span className="flex h-7 items-center gap-1.5 px-2 text-muted-foreground text-xs">
            <Check className="size-3.5" /> Resolved
          </span>
        ) : (
          onResolve !== undefined &&
          thread.viewerCanResolve && (
            <Button
              className="h-control-compact gap-1.5 px-2 text-xs"
              disabled={working}
              onClick={() => void resolve()}
              size="sm"
              variant="ghost"
            >
              <Check className="size-3.5" /> Resolve
            </Button>
          )
        )}
      </div>
    </div>
  );
}

function highlightReviewCode(content: string): ReactNode {
  const tokenPattern =
    /(\/\/.*$|#.*$|\/\*[\s\S]*?\*\/|'(?:\\.|[^'\\])*'|"(?:\\.|[^"\\])*"|`(?:\\.|[^`\\])*`|\b(?:async|await|break|case|catch|class|const|continue|default|else|export|extends|false|finally|for|from|function|if|import|in|interface|let|new|null|of|return|static|switch|throw|true|try|type|typeof|undefined|var|while)\b|\b\d+(?:\.\d+)?\b)/gm;
  // Keyed by where each token starts: the same word can come twice on a line.
  const nodes: ReactNode[] = [];
  let offset = 0;
  for (const token of content.split(tokenPattern)) {
    const start = offset;
    offset += token.length;
    if (token.length === 0) continue;
    const className = reviewTokenClass(token);
    nodes.push(
      className === null ? (
        token
      ) : (
        <span className={className} key={start}>
          {token}
        </span>
      ),
    );
  }
  return nodes;
}

function reviewTokenClass(token: string): string | null {
  if (/^(\/\/|#|\/\*)/.test(token)) return 'text-muted-foreground';
  if (/^['"`]/.test(token)) return 'text-sky-300';
  if (/^\d/.test(token)) return 'text-amber-300';
  if (/^[a-z]+$/.test(token)) return 'text-violet-300';
  return null;
}
